// JQ4-19 · US-CONFIG-005 — Comp (cortesía) dialog (slice 3)
//
// Reason-entry dialog for marking a ticket as a courtesy (comp).
// - Client-side guard: reason must be non-empty (UX only; server is authoritative min 1 char).
// - On confirm: calls onConfirm(reason); error display and open/close are parent-controlled.
// - On server error: parent sets errorMessage; dialog stays open until user fixes input or cancels.

import { useTranslation } from '@jq4race/i18n';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useState } from 'react';

interface CompDialogProps {
  open: boolean;
  ticketNumber: number;
  plate: string;
  /** Called with the reason string when the operator confirms */
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  /** Server-side error to surface inside the dialog */
  errorMessage?: string | null;
  /** Disables buttons while the mutation is in-flight */
  submitting?: boolean;
}

export function CompDialog({
  open,
  ticketNumber,
  plate,
  onConfirm,
  onCancel,
  errorMessage,
  submitting = false,
}: CompDialogProps) {
  const { t } = useTranslation('pickup-queue');
  const [reason, setReason] = useState('');

  const isValid = reason.trim().length > 0;

  function handleConfirm() {
    if (!isValid) return;
    onConfirm(reason.trim());
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setReason('');
      onCancel();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('compDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('compDialog.description', { plate, ticketNumber })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <label className="flex flex-col gap-1.5">
            <span className="font-sans text-sm font-medium text-foreground">
              {t('compDialog.reasonLabel')}
            </span>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('compDialog.reasonPlaceholder')}
              aria-label={t('compDialog.reasonAria')}
              maxLength={500}
              className="rounded-sm border border-input bg-background px-3 py-2 font-sans text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none"
            />
          </label>

          {errorMessage ? (
            <p role="alert" className="font-sans text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-sm px-4 py-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-60"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isValid || submitting}
            aria-label={t('compDialog.confirmAria')}
            className="rounded-sm bg-info px-4 py-2 text-sm font-medium text-info-foreground disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? t('compDialog.applying') : t('common:actions.confirm')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
